'use client'
import clsx from 'clsx'
import disableScroll from 'disable-scroll'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import AliceCarousel from 'react-alice-carousel'

import 'react-alice-carousel/lib/alice-carousel.css'
import s from './styles.module.scss'

import { defaultResponsive } from './defaultResponsive'
import { IconSvg } from '../../../components/atoms/icons'
import { breakpoints, useMediaQuery } from '../../../lib/hooks'
import { getCurrentScroll, ScrollType } from '../../../utils/get-current-scroll'

const getWindowSize = () => {
  if (typeof window === 'undefined') {
    return { innerWidth: 1024, innerHeight: 1024 }
  }
  const { innerWidth, innerHeight } = window
  return { innerWidth, innerHeight }
}

const Carousel = ({
  items,
  responsive = defaultResponsive,
  className,
  defaultNbItems = 5,
  arrowsDataGtm,
  onClickNextPrevButton,
  scrollAllVisibleSlides = true,
}: {
  items: ReactElement[]
  responsive?: any
  className?: string
  defaultNbItems?: number
  arrowsDataGtm?: string
  onClickNextPrevButton?: (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
  ) => void
  scrollAllVisibleSlides?: boolean
}) => {
  const [carouselIndex, setcarouselIndex] = useState<number>(0)
  const [scroll, setScroll] = useState<ScrollType>(ScrollType.NONE)

  const refCarousel = useRef<AliceCarousel>(null)
  const isMobile = useMediaQuery(breakpoints.newMobile)

  const [windowSize, setWindowSize] = useState({
    innerWidth: 1024,
    innerHeight: 1024,
  })
  const [nbItemsDisplayed, setNbItemsDisplayed] = useState<number>()
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false)

  const handleMultipleScrollNext = (e: any) => {
    e.stopPropagation()
    let newIndex =
      (refCarousel?.current?.props?.activeIndex ?? 0) + (nbItemsDisplayed ?? 0)
    newIndex =
      newIndex > items.length - (nbItemsDisplayed ?? 0)
        ? items.length - (nbItemsDisplayed ?? 0)
        : newIndex
    refCarousel?.current?.slideTo(newIndex)
  }

  const handleMultipleScrollPrev = (e: any) => {
    e.stopPropagation()
    let newIndex =
      (refCarousel?.current?.props?.activeIndex ?? 0) - (nbItemsDisplayed ?? 0)
    newIndex = newIndex < 0 ? 0 : newIndex
    refCarousel?.current?.slideTo(newIndex)
  }

  useEffect(() => {
    function handleWindowResize() {
      setWindowSize(getWindowSize())
    }

    setIsPageLoaded(true)
    setWindowSize(getWindowSize())

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  useEffect(() => {
    let nbItems = defaultNbItems
    for (const key in responsive) {
      if (windowSize.innerWidth <= Number(key)) {
        nbItems = responsive[key].items - 1
        break
      }
    }

    if (isMobile) {
      nbItems -= 1
    }

    setNbItemsDisplayed(nbItems)
  }, [windowSize])

  useEffect(() => {
    if (scroll === ScrollType.HORIZONTAL) {
      disableScroll.on()
    } else {
      disableScroll.off()
    }
  }, [scroll])

  useEffect(() => {
    if (refCarousel.current) {
      getCurrentScroll({ ref: refCarousel, setScroll })
    }
  }, [refCarousel])

  const renderItems = () => {
    if (isMobile) {
      return items
    }

    if (nbItemsDisplayed && items.length <= nbItemsDisplayed) {
      let emptyCardsNb = nbItemsDisplayed - items.length + 1
      if (isMobile) {
        emptyCardsNb += 1
      }

      return [...items, ...Array(emptyCardsNb).fill(<></>)]
    }

    return items
  }

  const getInnerWidth = () => {
    return !isPageLoaded ? windowSize.innerWidth : window.innerWidth
  }

  if (!isPageLoaded) {
    return null
  }

  return (
    <div
      className={clsx(s.carouselContainer, className, {
        [s.inlineCarousel]: !isMobile,
        [s.editoLikeCarousel]: isMobile,
      })}
      style={{ paddingBottom: 15 }}
    >
      <div className={s.carousel}>
        {!isMobile && <div className={s.hidePreviousItem}></div>}
        <AliceCarousel
          ref={refCarousel}
          innerWidth={getInnerWidth()}
          activeIndex={carouselIndex}
          onSlideChanged={(e) => {
            setcarouselIndex(e.item)
          }}
          mouseTracking={isMobile && scroll !== ScrollType.VERTICAL}
          touchTracking={isMobile && scroll !== ScrollType.VERTICAL}
          touchMoveDefaultEvents={true}
          disableButtonsControls={isMobile}
          animationDuration={500}
          infinite={false}
          disableDotsControls
          items={renderItems()}
          responsive={responsive}
          renderPrevButton={() => {
            if (nbItemsDisplayed && items.length <= nbItemsDisplayed) {
              return
            }
            if (carouselIndex === 0) {
              return
            }
            return (
              <IconSvg.ArrowLeft
                data-gtm={arrowsDataGtm ? `${arrowsDataGtm}_precedent` : ''}
                className={clsx(
                  s.circle,
                  s.arrowPrevAlternative,
                  s.alternativeArrow,
                )}
                onClick={(e) => {
                  if (scrollAllVisibleSlides) {
                    handleMultipleScrollPrev(e)
                  }
                  if (onClickNextPrevButton) {
                    onClickNextPrevButton(e)
                  }
                }}
              />
            )
          }}
          renderNextButton={() => {
            if (nbItemsDisplayed && items.length <= nbItemsDisplayed) {
              return
            }
            if (
              nbItemsDisplayed &&
              items.length - carouselIndex <= nbItemsDisplayed
            ) {
              return
            }
            return (
              <IconSvg.ArrowRight
                data-gtm={arrowsDataGtm ? `${arrowsDataGtm}_suivant` : ''}
                className={clsx(
                  s.circle,
                  s.arrowNextAlternative,
                  s.alternativeArrow,
                )}
                onClick={(e) => {
                  if (scrollAllVisibleSlides) {
                    handleMultipleScrollNext(e)
                  }
                  if (onClickNextPrevButton) {
                    onClickNextPrevButton(e)
                  }
                }}
              />
            )
          }}
        />
      </div>
    </div>
  )
}

export default Carousel
